import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Building2, Save, Loader2 } from "lucide-react";
import { calcInvoiceTotals, type InvoiceLineItem, type InvoiceInput } from "@/hooks/useInvoices";
import { useCompanySettings } from "@/hooks/useCompanySettings";

interface Props {
  initial?: Partial<InvoiceInput>;
  invoiceNumber: string;
  onSubmit: (data: InvoiceInput) => Promise<void>;
  onCancel: () => void;
  saving?: boolean;
}

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function InvoiceForm({ initial, invoiceNumber, onSubmit, onCancel, saving }: Props) {
  const [form, setForm] = useState<InvoiceInput>({
    proposal_id: initial?.proposal_id ?? null,
    industry: initial?.industry ?? "power-washing",
    invoice_number: initial?.invoice_number ?? invoiceNumber,
    status: initial?.status ?? "draft",
    client_name: initial?.client_name ?? "",
    client_company: initial?.client_company ?? "",
    client_email: initial?.client_email ?? "",
    client_address: initial?.client_address ?? "",
    issue_date: initial?.issue_date ?? new Date().toISOString().slice(0, 10),
    due_date: initial?.due_date ?? new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    line_items: initial?.line_items?.length ? initial.line_items : [{ description: "", quantity: 1, unit_price: 0 }],
    subtotal: 0,
    tax_rate: initial?.tax_rate ?? 0,
    tax_amount: 0,
    discount: initial?.discount ?? 0,
    total: 0,
    amount_paid: initial?.amount_paid ?? 0,
    notes: initial?.notes ?? "",
    payment_terms: initial?.payment_terms ?? "Net 30",
  });

  const totals = calcInvoiceTotals(form.line_items, form.tax_rate, form.discount);

  useEffect(() => {
    setForm((f) => ({ ...f, ...totals }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.line_items), form.tax_rate, form.discount]);

  const updateItem = (idx: number, patch: Partial<InvoiceLineItem>) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const addItem = () =>
    setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx: number) =>
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const { settings: company, setSettings: setCompany, isSaving: companySaving, saveSettings: saveCompany } = useCompanySettings();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Your Company Info</CardTitle>
          <CardDescription>Appears at the top of the invoice. Saved to your company settings and reused everywhere.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Company Name</Label>
            <Input value={company.company_name} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} placeholder="Your Company LLC" />
          </div>
          <div>
            <Label>Company Email</Label>
            <Input type="email" value={company.company_email} onChange={(e) => setCompany({ ...company, company_email: e.target.value })} placeholder="billing@company.com" />
          </div>
          <div>
            <Label>Company Phone</Label>
            <Input value={company.company_phone} onChange={(e) => setCompany({ ...company, company_phone: e.target.value })} placeholder="(555) 123-4567" />
          </div>
          <div>
            <Label>Logo URL</Label>
            <Input value={company.logo_url} onChange={(e) => setCompany({ ...company, logo_url: e.target.value })} placeholder="https://..." />
          </div>
          <div className="md:col-span-2">
            <Label>Company Address</Label>
            <Textarea rows={2} value={company.company_address} onChange={(e) => setCompany({ ...company, company_address: e.target.value })} placeholder="123 Main St, City, State ZIP" />
          </div>
          <div className="md:col-span-2">
            <Button type="button" size="sm" variant="outline" onClick={() => saveCompany(company)} disabled={companySaving}>
              {companySaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save Company Info
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Invoice Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Invoice Number</Label>
            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
          </div>
          <div>
            <Label>Issue Date</Label>
            <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input type="date" value={form.due_date ?? ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Input value={form.payment_terms ?? ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Bill To</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Client Name *</Label>
            <Input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
          </div>
          <div>
            <Label>Company</Label>
            <Input value={form.client_company ?? ""} onChange={(e) => setForm({ ...form, client_company: e.target.value })} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.client_email ?? ""} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
          </div>
          <div>
            <Label>Address</Label>
            <Input value={form.client_address ?? ""} onChange={(e) => setForm({ ...form, client_address: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Line Items</CardTitle>
          <Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="h-4 w-4 mr-1" />Add Item</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {form.line_items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-12 md:col-span-6">
                <Label className="text-xs">Description</Label>
                <Input value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} />
              </div>
              <div className="col-span-4 md:col-span-2">
                <Label className="text-xs">Qty</Label>
                <Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })} />
              </div>
              <div className="col-span-5 md:col-span-3">
                <Label className="text-xs">Unit Price</Label>
                <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })} />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(idx)} disabled={form.line_items.length === 1}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Totals</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tax Rate (%)</Label>
              <Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Discount ($)</Label>
              <Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} />
            </div>
          </div>
          <div className="border-t pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmt(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>-{fmt(form.discount)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{fmt(totals.tax_amount)}</span></div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t"><span>Total</span><span className="text-primary">{fmt(totals.total)}</span></div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={() => onSubmit({ ...form, ...totals })} disabled={saving || !form.client_name}>
          {saving ? "Saving..." : "Save Invoice"}
        </Button>
      </div>
    </div>
  );
}
